/**
 * Phase 7 — Sentry error monitoring (Feb 2026).
 *
 * NO-OP if REACT_APP_SENTRY_DSN is unset (safe for local dev / preview /
 * pilot until DSN is provisioned).
 *
 * Privacy:
 *   • sendDefaultPii: false — never auto-capture user emails/IPs
 *   • beforeSend strips Authorization/Cookie headers and any field whose
 *     name suggests sensitive content
 *   • URL query params named token/api_key/password/secret are redacted
 *
 * Sampling for the friends/family pilot:
 *   • 100% of errors
 *   • 5% of transactions (performance traces)
 *
 * Imported as the very first import in src/index.js.
 */
import * as Sentry from '@sentry/react';

const SENSITIVE_KEY_HINTS = ['password', 'token', 'secret', 'key', 'auth', 'cookie', 'credential'];
const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

function scrubObject(obj, depth = 0) {
  if (depth > 10 || obj === null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map((v) => scrubObject(v, depth + 1));
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    const kLower = (k || '').toLowerCase();
    if (SENSITIVE_KEY_HINTS.some((h) => kLower.includes(h))) {
      out[k] = '[REDACTED]';
    } else if (typeof v === 'string') {
      out[k] = v.replace(EMAIL_RE, '[EMAIL]');
    } else {
      out[k] = scrubObject(v, depth + 1);
    }
  }
  return out;
}

function beforeSend(event, hint) {
  // Phase 7-Lite — suppress known-noise events. These are transient mobile/network
  // issues, not application bugs. Same principle as the push.py warning-not-error fix.
  const exc = hint?.originalException;
  const msg = (event?.message || exc?.message || '').toString();
  const excType = exc?.name || event?.exception?.values?.[0]?.type || '';
  const noise = [
    'Script .../sw.js load failed',     // intermittent SW network failure (mobile Safari)
    'sw.js load failed',
    'Failed to register a ServiceWorker',
    'Failed to update a ServiceWorker',
    'ServiceWorker for scope',            // catch-all for SW register/update failures
    'An unknown error occurred when fetching the script',  // SW script fetch flap
    'load failed',                       // Safari's generic network-failure phrasing
    'Load failed',
    'NetworkError when attempting to fetch resource',
    'cancelled',                          // Safari's word for user-aborted fetches
    'AbortError',
    'The operation was aborted',
    // Phase 5.9.18 — additional noise patterns observed in prod:
    'Blocked a frame with origin',       // cross-origin iframe — Emergent badge / 3rd-party widgets
    'Permission denied to access property',  // same root cause as above
    'ResizeObserver loop',                // benign browser quirk, doesn't affect users
    'Non-Error promise rejection captured',
    'Java exception was raised',          // Android webview noise
    "Can't find variable: gmo",           // mobile Safari injected script noise
    "Can't find variable: webkit",
    'Network Error',                       // axios bubble from offline / flaky cell
    // Phase 5.9.19 — Radix Select + React 19 concurrent-mode portal cleanup race.
    // Harmless: caught by ErrorBoundary, no user-visible effect. Known upstream bug.
    "Failed to execute 'removeChild' on 'Node'",
    "The node to be removed is not a child of this node",
    "Failed to execute 'insertBefore' on 'Node'",
    // Feb 2026 — Browser extensions inject `<script>` blocks at the top of
    // every page; their global-scope errors get caught by our Sentry SDK
    // even though they have nothing to do with our app. Block the known
    // offenders by name. The generic stack-frame check below catches the
    // long tail.
    "Can't find variable: SCDynimacBridge",  // CapitalOne Shopping / similar coupon ext (typo'd in their code)
    "Can't find variable: SCDynamicBridge",  // same family, correctly spelled
    "Can't find variable: __SC_",            // generic SimplyCodes/CapitalOne shopping
    "Can't find variable: honey",
    "Can't find variable: Honey",
    "Can't find variable: HONEY",
    "Can't find variable: rakuten",
    "Can't find variable: __REACT_DEVTOOLS_GLOBAL_HOOK__",  // dev tools probe
    "Can't find variable: zaloJSV2",          // Zalo browser extension (Vietnam)
    "Can't find variable: ga",                // GA blocked by adblock
    "Can't find variable: gtag",              // gtag blocked by adblock
    "Can't find variable: fbq",               // Facebook pixel blocked by adblock
    "Can't find variable: _AutofillCallbackHandler",  // 1Password
    "Can't find variable: __pendo",           // Pendo analytics
    "Can't find variable: __wxjs_environment",
    'NPObject deleted',                       // legacy Flash NPAPI
    'window.webkit.messageHandlers',          // iOS WKWebView bridge probes
  ];
  if (noise.some((p) => msg.includes(p))) {
    return null;  // drop event
  }
  // Drop entire exception classes that are environmental, not app bugs.
  if (['SecurityError', 'NetworkError'].includes(excType)) {
    return null;
  }
  // Drop stale-cache ReferenceErrors — these only fire on old client bundles
  // where a recently-introduced top-level export hadn't shipped yet. A page
  // reload resolves them, no app-code action possible.
  if (excType === 'ReferenceError' && /is not defined/.test(msg)) {
    return null;
  }
  // Feb 2026 — generic browser-extension trap. Our compiled app code lives
  // exclusively in `/static/js/*.chunk.js` (or `/static/js/main.*.js`).
  // ANY exception whose topmost frame is `global code` AND whose source
  // file is the HTML route (e.g. `/register`, `/login`, `/`) was thrown by
  // a script the BROWSER itself injected — almost always a Chrome/Safari
  // extension (Honey, CapitalOne Shopping, password managers, ad-blockers
  // that try to hook globals before our React bundle loads). These cannot
  // be fixed in our app code and they pollute the triage queue.
  try {
    const frames = event?.exception?.values?.[0]?.stacktrace?.frames || [];
    const top = frames[frames.length - 1];  // topmost = last in Sentry's frames array
    const topFn = (top?.function || '').toLowerCase();
    const topFile = top?.filename || top?.abs_path || '';
    const looksLikeRoute = /^https?:\/\/[^/]+\/[^.]*$/.test(topFile) && !/\.js(\?|$)/.test(topFile);
    if (topFn === 'global code' && looksLikeRoute) {
      return null;
    }
  } catch (_) {
    /* never let the filter itself throw */
  }
  // Safari-style "Can't find variable: X" without our bundle in the stack:
  // if the only frames are inline scripts on the HTML page, the variable
  // was referenced by an injected extension, not by us.
  if (/^Can't find variable: /.test(msg)) {
    try {
      const frames = event?.exception?.values?.[0]?.stacktrace?.frames || [];
      const ourFrames = frames.filter((f) => /\/static\/js\//.test(f?.filename || f?.abs_path || ''));
      if (frames.length > 0 && ourFrames.length === 0) {
        return null;
      }
    } catch (_) {
      /* fall through to send the event */
    }
  }

  // 1. user → keep id only
  if (event.user) {
    const id = event.user.id;
    event.user = id ? { id } : {};
  }
  // 2. request headers + url
  if (event.request) {
    if (event.request.headers) {
      for (const k of Object.keys(event.request.headers)) {
        if (['authorization', 'cookie', 'x-api-key', 'x-auth-token'].includes(k.toLowerCase())) {
          event.request.headers[k] = '[REDACTED]';
        }
      }
    }
    if (event.request.url) {
      try {
        const u = new URL(event.request.url);
        for (const p of ['token', 'api_key', 'password', 'secret']) {
          if (u.searchParams.has(p)) u.searchParams.set(p, '[REDACTED]');
        }
        event.request.url = u.toString();
      } catch (_) {
        /* ignore malformed URLs */
      }
    }
    if (event.request.data) event.request.data = scrubObject(event.request.data);
  }
  // 3. breadcrumbs
  if (Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs = event.breadcrumbs.map((b) => ({
      ...b,
      data: b.data ? scrubObject(b.data) : b.data,
      message: typeof b.message === 'string' ? b.message.replace(EMAIL_RE, '[EMAIL]') : b.message,
    }));
  }
  return event;
}

export function initSentry() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) {
    // eslint-disable-next-line no-console
    console.info('Sentry: REACT_APP_SENTRY_DSN not set — error monitoring DISABLED');
    return false;
  }

  const env = process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV || 'development';
  const release = process.env.REACT_APP_VERSION || 'ash-and-merch@unknown';
  const tracesRate = parseFloat(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || '0.05');

  Sentry.init({
    dsn,
    environment: env,
    release,
    sendDefaultPii: false,
    tracesSampleRate: Number.isFinite(tracesRate) ? tracesRate : 0.05,
    integrations: [Sentry.browserTracingIntegration()],
    beforeSend,
  });
  // eslint-disable-next-line no-console
  console.info(`Sentry: initialised (env=${env}, release=${release}, traces=${tracesRate})`);
  return true;
}

// Auto-initialise on module import so users of this file just need to import it once.
initSentry();

// Expose for browser-console access (handy for "trigger a test event" verification
// and ad-hoc error capture from third-party scripts/copy-paste utilities).
if (typeof window !== 'undefined') {
  window.Sentry = Sentry;
}

export { Sentry };
